import { Suspense, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLazyLoadQuery } from "react-relay"
import { useDebounce } from "@uidotdev/usehooks"
import { isEqual } from "es-toolkit/predicate"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContentCustom, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { SearchInput } from "@/components/ui/search"
import { usePatientLayoutContext } from "@/contexts/patient-layout-context"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import { joinStrings } from "@/lib/joinString"
import { BooleanSelect } from "@/modules/care-provider/advanced-search/boolean-select"
import {
  AddressOption,
  CareProviderAddressSelect,
} from "@/modules/care-provider/advanced-search/care-provider-address-filter/care-provider-address-select"
import { CareProviderResult } from "@/modules/care-provider/advanced-search/care-provider-result"
import { ADVANCED_CARE_PROVIDER_FILTER_MENU } from "@/modules/care-provider/advanced-search/constants/advanced-care-provider-filter-menu"
import { CARE_PROVIDER_PRIORITY_LANGUAGES } from "@/modules/care-provider/advanced-search/constants/care-provider-priority-languages"
import { MultipleSelect } from "@/modules/care-provider/advanced-search/multiple-select"
import { useAdvancedCareProviderSearchFiltersQuery } from "@/modules/care-provider/advanced-search/services/__generated__/useAdvancedCareProviderSearchFiltersQuery.graphql"
import { advancedCareProviderSearchFiltersQuery } from "@/modules/care-provider/advanced-search/services/useAdvancedCareProviderSearchFiltersQuery"
import { SingleSelect } from "@/modules/care-provider/advanced-search/single-select"
import {
  AdvancedCareProviderFilter,
  AdvancedCareProviderFilterType,
  FEATURE,
  FEATURES,
  FilterSelectValues,
  FormattedAdvancedCareProviderFilterValues,
  OfficialLanguage,
} from "@/modules/care-provider/advanced-search/types/AdvancedCareProviderFilter"
import { type ApplicableInsuranceCareProviderListIds } from "@/modules/care-provider/simple-search/services/useCareProviderQuickListQuery"
import { formatPastedCareProviderPhoneNumber } from "@/modules/care-provider/utils/format-pasted-care-provider-phone-number"
import { useCareProviderManualAddressStoreContext } from "@/stores/useCareProviderManualAddressStore"
import { CareProviderGroupType } from "@/types/__generated__/union-types"

export type FilterValues = Partial<FormattedAdvancedCareProviderFilterValues>

const pickValidOption = (
  current: FilterSelectValues | undefined,
  options: readonly { id: string }[] | undefined,
): FilterSelectValues | undefined => (current && options?.some((opt) => opt.id === current.id) ? current : undefined)

export type OfficialLanguages = {
  id: string
  isoLanguageCode: string
  officialLanguageLookupId: number
  name: string
}[]

export type DefaultAdvancedCareProviderSearchFilters = {
  groupType?: CareProviderGroupType
  hasMPNReferral?: boolean
  isWalkIn?: boolean
  specialtyId?: string | null
}

type AdvancedCareProviderSearchDialogProps = {
  defaultFilters?: DefaultAdvancedCareProviderSearchFilters
  applicableInsuranceCareProviderListIds: ApplicableInsuranceCareProviderListIds
  selectedCareProviderIds: string[]
  onClose: VoidFunction
  onCareProviderSelect: (relationId: string) => void
}

const AdvancedCareProviderSearchDialog = ({
  defaultFilters,
  applicableInsuranceCareProviderListIds,
  selectedCareProviderIds,
  onClose,
  onCareProviderSelect,
}: AdvancedCareProviderSearchDialogProps) => {
  const { t } = useTranslation()
  const hasApplicableInsuranceCareProviderList = applicableInsuranceCareProviderListIds.length > 0
  const currentLanguage = useCurrentLanguage().toUpperCase()
  const [searchValue, setSearchValue] = useState("")
  const [isDisabledResetButton, setIsDisabledResetButton] = useState(true)
  const [filterValues, setFilterValues] = useState<FilterValues | undefined>(undefined)
  const debouncedKeyword = useDebounce(searchValue, 500)
  const debouncedFilterValues = useDebounce(filterValues, 500)
  const [fetchKey, setFetchKey] = useState<number>(0)
  const { countryPatient } = usePatientLayoutContext()
  const { city, country, houseNumberStreet, postalCode } = countryPatient?.address || {}
  const manualAddresses = useCareProviderManualAddressStoreContext((state) => state.addresses)
  const postalCodeCity = joinStrings([postalCode, city], " ")
  const patientAddress = joinStrings(
    [
      houseNumberStreet,
      postalCodeCity,
      country?.countryLookupTranslations.find((item) => item.language === currentLanguage)?.countryName,
    ],
    ", ",
  )

  const options: AddressOption[] = [
    {
      label: patientAddress,
      streetName: houseNumberStreet ?? "",
      postalCode: postalCode ?? "",
      municipality: city ?? "",
      countryCode: country?.code ?? "",
    },
    ...(manualAddresses ?? []),
  ]

  const [address, setAddress] = useState<AddressOption | null>(options[0])

  const { careProviderGroups, cantons, officialLanguages, diagnosticFacilities, certificates } =
    useLazyLoadQuery<useAdvancedCareProviderSearchFiltersQuery>(advancedCareProviderSearchFiltersQuery, {
      where: {
        and: [
          {
            searchTypes: {
              some: {
                eq: "ADVANCE",
              },
            },
            parentId: {
              eq: null,
            },
          },
        ],
      },
    })

  const formattedFeatures: FEATURE[] = FEATURES.map((item) => {
    return {
      id: item,
      name: t(`admin.careProviders.advancedSearch.filter.features.options.${item}`),
    }
  })

  const formattedOptions = useCallback(
    (options?: AdvancedCareProviderFilter[Exclude<keyof AdvancedCareProviderFilter, "features">]) => {
      if (!options) return []

      return options
        .map((item) => ({
          id: item.id,
          name: item.translations.find((t) => t.language === currentLanguage)?.name ?? "",
          ...("groupType" in item ? { groupType: item.groupType } : {}),
          ...("cantonCode" in item ? { cantonCode: item.cantonCode } : {}),
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    [currentLanguage],
  )

  const [topLanguages, otherLanguages] = officialLanguages.reduce<[OfficialLanguage[], OfficialLanguage[]]>(
    ([top, others], item: OfficialLanguage) => {
      if (CARE_PROVIDER_PRIORITY_LANGUAGES.includes(item.isoLanguageCode)) {
        top.push(item)
      } else {
        others.push(item)
      }
      return [top, others]
    },
    [[], []],
  )

  const sortedTopLanguages = topLanguages.toSorted(
    (a, b) =>
      CARE_PROVIDER_PRIORITY_LANGUAGES.indexOf(a.isoLanguageCode) -
      CARE_PROVIDER_PRIORITY_LANGUAGES.indexOf(b.isoLanguageCode),
  )

  const sortedOtherLanguages = otherLanguages.toSorted((a, b) => a.isoLanguageCode.localeCompare(b.isoLanguageCode))
  const sortedAllLanguages = useMemo(
    () => [...sortedTopLanguages, ...sortedOtherLanguages],
    [sortedTopLanguages, sortedOtherLanguages],
  )

  const formattedOfficialLanguages = sortedAllLanguages.map((item) => ({
    id: item.id,
    name: item.translations.find((t) => t.language === currentLanguage)?.name ?? "",
  }))

  const formattedFilters = useMemo(() => {
    const groups = careProviderGroups?.items ?? []
    const selectedGroupId = filterValues?.careProviderGroups?.id
    const selectedGroup = groups.find((item) => item.id === selectedGroupId)

    const defaultSpecialties =
      careProviderGroups?.items?.flatMap((group) =>
        group.specialties?.filter((specialty) => specialty.translations?.length > 0),
      ) ?? []

    const uniqueDefaultSpecialties = Array.from(new Map(defaultSpecialties.map((item) => [item.id, item])).values())

    const specialties = selectedGroupId ? selectedGroup?.specialties : uniqueDefaultSpecialties

    const selectedSpecialty = specialties?.find((item) => item.id === filterValues?.specialties?.id)
    return {
      careProviderGroups: [...formattedOptions(groups)],
      specialties: formattedOptions(specialties),
      foci: formattedOptions(selectedSpecialty?.foci),
      specializations: formattedOptions(selectedSpecialty?.specializations),
      subjectAreas: formattedOptions(selectedGroup?.subjectAreas),
      services: formattedOptions(selectedGroup?.services),
      cantons: formattedOptions(cantons),

      features: formattedFeatures,
      certificates: formattedOptions(certificates),
      diagnosticFacilities: formattedOptions(diagnosticFacilities),
      officialLanguages: formattedOfficialLanguages,
    }
  }, [
    formattedFeatures,
    cantons,
    diagnosticFacilities,
    careProviderGroups,
    certificates,
    formattedOfficialLanguages,
    filterValues?.careProviderGroups?.id,
    filterValues?.specialties?.id,
    formattedOptions,
  ])

  const defaultFilterValues: FilterValues | undefined = useMemo(() => {
    const { careProviderGroups, specialties } = formattedFilters

    const defaultGroup = careProviderGroups.find((item) => item.groupType === defaultFilters?.groupType)
    const defaultSpecialty = specialties.find((item) => item.id === defaultFilters?.specialtyId)

    return {
      isMedgatePartnerNetworkMember: defaultFilters?.hasMPNReferral ?? false,
      isInsurerNetworkMember: hasApplicableInsuranceCareProviderList,
      isWalkIn: defaultFilters?.isWalkIn ?? false,
      ...(defaultSpecialty ? { specialties: defaultSpecialty } : {}),
      ...(defaultGroup ? { careProviderGroups: defaultGroup } : {}),
    }
  }, [defaultFilters, formattedFilters, hasApplicableInsuranceCareProviderList])

  useEffect(() => {
    if (!filterValues && defaultFilterValues) {
      setFilterValues(defaultFilterValues)
    }
  }, [defaultFilterValues, filterValues])

  const handleChange = (
    key: AdvancedCareProviderFilterType,
    value?: FilterSelectValues | FilterSelectValues[] | boolean | null,
    analyticsSuffixKey?: string,
  ) => {
    setFilterValues((prev) => {
      const newValues: FilterValues = { ...prev, [key]: value }

      if (key === "careProviderGroups" || key === "specialties") {
        const groups = careProviderGroups?.items ?? []
        const selectedGroup = groups.find((group) => group.id === newValues.careProviderGroups?.id)
        const specialtyOptions = newValues.careProviderGroups?.id
          ? selectedGroup?.specialties
          : groups.flatMap((group) => group.specialties ?? [])

        newValues.specialties = pickValidOption(newValues.specialties, specialtyOptions)
        const selectedSpecialty = specialtyOptions?.find((s) => s.id === newValues.specialties?.id)

        newValues.foci = pickValidOption(newValues.foci, selectedSpecialty?.foci)
        newValues.specializations = pickValidOption(newValues.specializations, selectedSpecialty?.specializations)
        newValues.subjectAreas = pickValidOption(newValues.subjectAreas, selectedGroup?.subjectAreas)
        newValues.services = pickValidOption(newValues.services, selectedGroup?.services)
      }

      setIsDisabledResetButton(isEqual(defaultFilterValues, newValues))
      return newValues
    })
    setFetchKey((prev) => prev + 1)

    if (analyticsSuffixKey) FaroInsightsWrapper.pushEvent(`point_of_care_advance_search_${analyticsSuffixKey}`)
  }

  const handleFilterSelect = (value: string, search: string, options: FilterSelectValues[]) => {
    const keyword = search.toLowerCase()
    const found = options.find((i) => i.id.toLowerCase() === value)
    const isMatch = found?.name.toLowerCase().includes(keyword)

    return isMatch ? 1 : 0
  }

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (e.target instanceof HTMLInputElement) {
      e.preventDefault()
      const pastedText = e.clipboardData.getData("text")
      const formattedText = formatPastedCareProviderPhoneNumber(pastedText)

      const { value, selectionStart, selectionEnd } = e.target

      if (selectionStart === null || selectionEnd === null) return

      const newValue = value.substring(0, selectionStart) + formattedText + value.substring(selectionEnd)
      setSearchValue(newValue)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContentCustom
        variant="large"
        data-testid="care-provider-advanced-search-dialog"
        className="bg-surface shadow-5 grid w-full rounded-xl text-left align-middle opacity-100 transition-all"
        onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t("admin.careProviders.advancedSearch.title")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col">
          <div className="grid">
            <div className="bg-surface left-0 w-full min-w-0 px-6 pt-3">
              <div className="flex flex-wrap gap-2">
                {ADVANCED_CARE_PROVIDER_FILTER_MENU.entries().map(
                  ([key, { label, type, isSearchable, analyticsSuffixKey }]) => {
                    if (key === "isInsurerNetworkMember" && !hasApplicableInsuranceCareProviderList) return null

                    const selectedOption: FilterSelectValues | FilterSelectValues[] | boolean | undefined =
                      filterValues?.[key]
                    const options = formattedFilters[key as keyof typeof formattedFilters]

                    if (options?.length > 0) {
                      if (
                        type === "singleSelect" &&
                        (selectedOption === undefined || typeof selectedOption === "object") &&
                        !Array.isArray(selectedOption)
                      ) {
                        const selectedLabel = selectedOption?.name ?? t(label)
                        return (
                          <SingleSelect
                            key={key}
                            data-testid={key}
                            label={selectedLabel}
                            options={options}
                            value={selectedOption ?? null}
                            isSearchable={isSearchable}
                            getOptionLabel={(option) => option?.name}
                            getOptionValue={(option) => option?.id}
                            isOptionSelected={(option, selectedOption) => option.id === selectedOption?.id}
                            onChange={(value) => handleChange(key, value, analyticsSuffixKey)}
                            onFilter={(value, search) => handleFilterSelect(value, search, options)}
                          />
                        )
                      }

                      if (
                        type === "multipleSelect" &&
                        (selectedOption === undefined || Array.isArray(selectedOption))
                      ) {
                        return (
                          <MultipleSelect
                            key={key}
                            data-testid={key}
                            label={label}
                            options={options}
                            value={selectedOption ?? []}
                            isSearchable={isSearchable}
                            getOptionLabel={(option) => option?.name}
                            getOptionValue={(option) => option?.id}
                            isOptionSelected={(option, selectedOptions) =>
                              !!selectedOptions?.find((selectedOption) => selectedOption.id === option.id)
                            }
                            dividerIndex={
                              key === "officialLanguages" ? CARE_PROVIDER_PRIORITY_LANGUAGES.length - 1 : undefined
                            }
                            onChange={(value) => {
                              const selectedOptions = selectedOption?.find((item) => item.id === value.id)
                                ? selectedOption?.filter((v) => v.id !== value.id)
                                : [value, ...(selectedOption ?? [])]
                              handleChange(key, selectedOptions, analyticsSuffixKey)
                            }}
                            onFilter={(value, search) => handleFilterSelect(value, search, options)}
                            onReset={() => handleChange(key, undefined)}
                          />
                        )
                      }
                    }

                    if (type === "booleanSelect")
                      return (
                        <BooleanSelect
                          checked={!!selectedOption}
                          key={key}
                          label={label}
                          data-testid={key}
                          onCheckedChange={(value: boolean) => handleChange(key, value, analyticsSuffixKey)}
                        />
                      )
                  },
                )}

                <Button
                  disabled={isDisabledResetButton}
                  variant="tertiary"
                  className="h-8"
                  onClick={() => {
                    setFilterValues(undefined)
                    setIsDisabledResetButton(true)
                    setFetchKey((prev) => prev + 1)
                  }}>
                  {t("admin.careProviders.advancedSearch.filter.reset")}
                </Button>
              </div>

              <div className="flex w-full items-center justify-between py-4">
                <SearchInput
                  data-testid="advanced-care-provider-search-input"
                  value={searchValue}
                  placeholder={t("common.search")}
                  autoComplete="off"
                  onClear={() => setSearchValue("")}
                  onChange={({ target }) => setSearchValue(target.value)}
                  onPaste={onPaste}
                  autoFocus={false}
                />
                <div className="flex items-center">
                  <span className="text-on-surface-subdued mr-2 text-sm">
                    {t("admin.careProviders.advancedSearch.filter.sortByDistanceFrom")}:
                  </span>
                  <div className="flex min-w-0 flex-1 items-center">
                    <CareProviderAddressSelect
                      value={address}
                      options={options}
                      onChange={(selectedItem) => setAddress(selectedItem)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <Suspense fallback={<LoadingSpinner isFullPage size={32} className="mb-[43%]" />}>
              <CareProviderResult
                filterValues={debouncedFilterValues ?? defaultFilterValues}
                keyword={debouncedKeyword.trim()}
                fetchKey={fetchKey}
                currentAddress={address}
                officialLanguages={officialLanguages}
                hasMPNReferral={defaultFilters?.hasMPNReferral}
                applicableInsuranceCareProviderListIds={applicableInsuranceCareProviderListIds}
                selectedCareProviderIds={selectedCareProviderIds}
                onCareProviderSelect={onCareProviderSelect}
              />
            </Suspense>
          </div>
        </div>
        <DialogFooter className="flex justify-end gap-2 border-t px-6 pt-4 pb-6">
          <Button onClick={onClose} variant="secondary">
            {t("common.button.close")}
          </Button>
        </DialogFooter>
      </DialogContentCustom>
    </Dialog>
  )
}

export { AdvancedCareProviderSearchDialog }
